import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to manage navbar visibility based on scroll and touch events
 * @param {boolean} isMobile - Whether the current device is mobile phone
 * @param {string} currentOrientation - Current device orientation ('portrait' or 'landscape')
 * @param {boolean} isFocusMode - Whether the app is in focus mode
 * @param {boolean} isMobileOrTablet - Whether the device is mobile or tablet (touch device)
 * @returns {boolean} hideNavbar - Whether the navbar should be hidden
 */
const useNavbarVisibility = (isMobile, currentOrientation, isFocusMode, isMobileOrTablet = false) => {
  // Use isMobileOrTablet for touch behaviors, fallback to isMobile if not provided
  const isTouchDevice = isMobileOrTablet || isMobile;
  const [hideNavbar, setHideNavbar] = useState(false);
  const scrollTimerRef = useRef(null);
  
  // Handle wheel scroll events
  useEffect(() => {
    const handleScrollChange = (direction, delta) => {
      // Process for: mobile portrait OR tablet (any orientation) OR desktop.
      // Desktop is wheel-driven (deltaY direction) — parity with the Astro pill's
      // hide-on-scroll-down / show-on-scroll-up.
      const shouldProcess = (isMobile && currentOrientation === 'portrait') ||
                           (isTouchDevice && !isMobile) || // Tablet in any orientation
                           (!isMobile && !isTouchDevice);  // Desktop
      if (!shouldProcess) return;

      // Ignore scroll on empty chatty page (non-scrollable)
      const isEmptyChatty = document.body.classList.contains('chatty-page') &&
                           document.querySelector('#chat-input-fixed.is-centered');
      if (isEmptyChatty) return;

      // Ignore tiny movements
      if (delta < 8) return;
      
      // Clear any existing timeout
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
      
      // Special case: if at top and scrolling up, show navbar (iOS Safari fallback)
      const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop < 20 && direction === "up") {
        setHideNavbar(false);
        scrollTimerRef.current = null;
        return;
      }

      // Set a timeout for normal scroll behavior
      const delay = 100;

      scrollTimerRef.current = setTimeout(() => {
        // Simple direction-based control
        if (direction === "down") {
          setHideNavbar(true);
        } else if (direction === "up") {
          setHideNavbar(false);
        }

        // Clear the timer reference
        scrollTimerRef.current = null;
      }, delay);
    };

    const handleWheel = (event) => {
      // Scroll direction: up or down
      const direction = event.deltaY > 0 ? 'down' : 'up';
      const delta = Math.abs(event.deltaY); // Get the delta of the scroll
      handleScrollChange(direction, delta);
    };

    window.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [isMobile, isTouchDevice, currentOrientation]);

  // Handle touch events for mobile/tablet (iOS Safari - immediate response during touch)
  useEffect(() => {
    // Enable for: mobile portrait OR tablet (any orientation)
    const shouldEnable = (isMobile && currentOrientation === 'portrait') ||
                        (isTouchDevice && !isMobile);
    if (!shouldEnable || isFocusMode) return;

    let startY = null;
    let totalDown = 0;
    let totalUp = 0;

    const handleTouchStart = (e) => {
      // Ignore on empty chatty page (non-scrollable)
      const isEmptyChatty = document.body.classList.contains('chatty-page') &&
                           document.querySelector('#chat-input-fixed.is-centered');
      if (isEmptyChatty) return;

      startY = e.touches[0].clientY;
      totalDown = 0;
      totalUp = 0;
    };

    const handleTouchMove = (e) => {
      // Ignore on empty chatty page
      const isEmptyChatty = document.body.classList.contains('chatty-page') &&
                           document.querySelector('#chat-input-fixed.is-centered');
      if (isEmptyChatty || startY === null) return;

      const currentY = e.touches[0].clientY;
      const deltaY = startY - currentY;

      if (deltaY > 0) {
        totalDown = deltaY;
      } else {
        totalUp = Math.abs(deltaY);
      }

      // Hide: need 80px down movement with minimal up
      if (totalDown > 80 && totalUp < 20) {
        setHideNavbar(true);
      }
      // Show: need 40px up movement
      if (totalUp > 40) {
        setHideNavbar(false);
      }
    };

    const handleTouchEnd = () => {
      startY = null;
      totalDown = 0;
      totalUp = 0;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, isTouchDevice, currentOrientation, isFocusMode]);

  // Handle all scroll events with Safari-optimized logic
  useEffect(() => {
    // Enable for: mobile portrait OR tablet (any orientation)
    const shouldEnable = (isMobile && currentOrientation === 'portrait') ||
                        (isTouchDevice && !isMobile);
    if (!shouldEnable || isFocusMode) return;

    let lastScrollTop = 0;
    let ticking = false;
    const delta = 5; // Minimum scroll delta before triggering

    const handleScroll = () => {
      if (ticking) return;

      // Ignore scroll on empty chatty page (non-scrollable)
      const isEmptyChatty = document.body.classList.contains('chatty-page') &&
                           document.querySelector('#chat-input-fixed.is-centered');
      if (isEmptyChatty) return;

      ticking = true;

      requestAnimationFrame(() => {
        const currentScrollTop = document.body.scrollTop || window.scrollY || window.pageYOffset || 0;

        // Make sure they scroll more than delta to prevent jitter
        if (Math.abs(lastScrollTop - currentScrollTop) <= delta) {
          ticking = false;
          return;
        }

        // Check if page is scrollable (use documentElement for iOS Safari compatibility)
        const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        const viewHeight = window.innerHeight || document.documentElement.clientHeight;
        const isScrollable = docHeight > viewHeight + 50; // 50px buffer for iOS Safari address bar

        // Check if at top of page
        const isAtTop = currentScrollTop < 20;

        // Always show navbar when at top (handles momentum scroll)
        if (isAtTop) {
          setHideNavbar(false);
          lastScrollTop = currentScrollTop;
          ticking = false;
          return;
        }

        // Only hide navbar if scrolling down AND page is scrollable AND not at top
        if (currentScrollTop > lastScrollTop && isScrollable) {
          // Scroll Down - hide navbar
          setHideNavbar(true);
        } else if (currentScrollTop < lastScrollTop) {
          // Scroll Up - show navbar (any speed)
          setHideNavbar(false);
        }

        lastScrollTop = currentScrollTop;
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, isTouchDevice, currentOrientation, isFocusMode]);

  // Always show navbar in focus mode
  useEffect(() => {
    if (isFocusMode) {
      setHideNavbar(true);
    }
  }, [isFocusMode]);

  return hideNavbar;
};

export default useNavbarVisibility;